// src/components/PhaseDetails/hooks/usePhaseData.ts
import { useState, useEffect, useRef, useCallback } from 'react';
import { phaseDetailsService } from '../service';
import { GRID_PAGE_SIZE } from '@components/GridView/types';
import type { GridViewRequestParams } from '@components/GridView/types';
import type { ConversationDetail } from '@/types/conversation';

// ==================== Normalization ====================

/**
 * Normalizes a phase conversation from the API response.
 * Handles both CustomFieldLayers (phase endpoint) and LayerCustomFields naming variants.
 * Preserves ConversationPhaseId and CoreTaskPhaseId which are phase-specific.
 *
 * Also normalizes fields used by GridTitleCell:
 *  - Icon: maps SideMenuIconClass → Icon if Icon is missing
 *  - ConversationLayers: derived from layer data if not returned by the API
 */
function normalizePhaseConversation(conv: any): ConversationDetail
{
  const layers: any[] = conv.CustomFieldLayers ?? conv.LayerCustomFields ?? [];
  const layerCustomFields = layers
    .filter((l: any) => Array.isArray(l.CustomFields) && l.CustomFields.length > 0)
    .map((l: any) => ({
      LayerId: l.LayerId,
      LayerName: l.LayerName ?? l.Name,
      Color: l.Color,
      CustomFields: l.CustomFields.map((cf: any) => ({
        CustomFieldValueId: cf.CustomFieldValueId,
        CustomFieldId: cf.CustomFieldId,
        FieldValue: cf.SelectedValuesText ?? cf.Value ?? cf.FieldValue,
        Value: cf.Value,
        FieldName: (cf.Name ?? cf.FieldName ?? '').trim(),
        FieldTypeId: cf.FieldTypeId,
        LayerId: cf.LayerId,
        ConversationId: cf.ConversationId ?? conv.ConversationId,
        ServiceUrl: cf.ServiceUrl,
        TextField: cf.TextField,
        ValueField: cf.ValueField,
        OrderInLayer: cf.OrderInLayer,
        SummatoryValuePhases: cf.SummatoryValuePhases ?? false,
        SummatoryPeoplePhases: cf.SummatoryPeoplePhases ?? false,
      })),
    }));

  // Icon: the Phase Details API may return SideMenuIconClass instead of Icon
  const icon: string = conv.Icon || conv.SideMenuIconClass || '';

  // ConversationLayers: used by GridTitleCell for the colored layer-dot indicators.
  // If the API doesn't return the array, derive it from the raw layer data so the
  // dots still render with the correct colors.
  const conversationLayers: any[] =
    Array.isArray(conv.ConversationLayers) && conv.ConversationLayers.length > 0
      ? conv.ConversationLayers
      : layers.map((l: any) => ({
        ConversationLayerId: null,
        ConversationId: conv.ConversationId,
        LayerId: l.LayerId,
        LayerName: l.LayerName ?? l.Name ?? '',
        LayerColor: l.Color ?? '',
        IsDefault: false,
        PriorityInConfigValue: 0,
        WasEdited: null,
        IsActive: true,
      }));

  return {
    ...conv,
    Icon: icon,
    ConversationLayers: conversationLayers,
    LayerCustomFields: layerCustomFields,
    ConversationPhaseId: conv.ConversationPhaseId ?? null,
    CoreTaskPhaseId: conv.CoreTaskPhaseId ?? null,
  } as ConversationDetail;
}

// ==================== Options ====================

interface UsePhaseDataOptions {
  dashboardId: number;
  configValueId?: number;
  projectId?: string;
  filters?: string;
  searchText?: string;
  layersToHide?: string;
  isActive?: boolean;
  extraFilterParams?: Record<string, any>;
}

// ==================== Return ====================

interface UsePhaseDataReturn {
  conversations: ConversationDetail[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  totalItems: number;
  loadMore: () => void;
  refresh: () => void;
  updateField: (
    conversationId: number,
    layerId: number,
    customFieldId: number,
    value: any,
    display: string,
    /** Phase row to update. Omit to update every row of the conversation. */
    conversationPhaseId?: number | null
  ) => void;
  updateConversationState: (conversationId: number, updates: Partial<ConversationDetail>) => void;
  removeConversation: (conversationId: number) => void;
}

// ==================== Hook ====================

export function usePhaseData({
  dashboardId,
  configValueId,
  projectId,
  filters,
  searchText,
  layersToHide,
  isActive = true,
  extraFilterParams,
}: UsePhaseDataOptions): UsePhaseDataReturn
{
  const [conversations, setConversations] = useState<ConversationDetail[]>([]);
  const [loading, setLoading] = useState(isActive);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const isFetchingRef = useRef(false);
  const loadedCountRef = useRef(0);

  const buildParams = useCallback(
    (page: number): GridViewRequestParams => ({
      CustomFields: [],
      PageNumber: page,
      PageSize: GRID_PAGE_SIZE,
      DashboardId: dashboardId,
      Project: projectId ?? '',
      Filters: filters ?? '',
      SearchText: searchText ?? '',
      LayersToHide: layersToHide ?? '',
      DashboardViewTypeId: 9,
      IsFilterPersonal: false,
      ...(configValueId != null && { ConfigValueId: configValueId }),
      ...extraFilterParams,
    }),
    [dashboardId, configValueId, projectId, filters, searchText, layersToHide, extraFilterParams],
  );

  const fetchInitial = useCallback(async () =>
  {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);

    try
    {
      const params = buildParams(1);
      const response = await phaseDetailsService.getPhaseDetailsConversations(params);
      const items = (response.Conversations ?? []).map(normalizePhaseConversation);

      const total = response.TotalItems ?? 0;
      const computedHasMore = total > 0 ? items.length < total : items.length >= GRID_PAGE_SIZE;

      setConversations(items);
      setTotalItems(total);
      setPageNumber(1);
      loadedCountRef.current = items.length;
      setHasMore(computedHasMore);
    }
    finally
    {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [buildParams]);

  const loadMore = useCallback(async () =>
  {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    setLoadingMore(true);

    try
    {
      const nextPage = pageNumber + 1;
      const params = buildParams(nextPage);
      const response = await phaseDetailsService.getPhaseDetailsConversations(params);
      const newItems = (response.Conversations ?? []).map(normalizePhaseConversation);

      if (newItems.length === 0)
      {
        setHasMore(false);
        return;
      }

      setConversations(prev => [...prev, ...newItems]);
      loadedCountRef.current += newItems.length;

      const responseTotal = response.TotalItems ?? 0;
      const computedHasMore =
        responseTotal > 0
          ? loadedCountRef.current < responseTotal
          : newItems.length >= GRID_PAGE_SIZE;

      setTotalItems(responseTotal);
      setPageNumber(nextPage);
      setHasMore(computedHasMore);
    }
    finally
    {
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [buildParams, hasMore, pageNumber]);

  useEffect(() =>
  {
    if (!isActive) return;
    fetchInitial();
  }, [
    isActive,
    dashboardId,
    configValueId,
    projectId,
    filters,
    searchText,
    layersToHide,
    extraFilterParams,
  ]);

  const updateField = useCallback(
    (
      conversationId: number,
      layerId: number,
      customFieldId: number,
      value: any,
      display: string,
      conversationPhaseId?: number | null,
    ) =>
    {
      setConversations(prev =>
        prev.map(conv =>
        {
          if (conv.ConversationId !== conversationId) return conv;
          // One conversation spans several rows here, so match the phase to avoid
          // leaking the value into its sibling rows.
          if (
            conversationPhaseId !== undefined &&
            (conv.ConversationPhaseId ?? 0) !== (conversationPhaseId ?? 0)
          )
          {
            return conv;
          }
          return {
            ...conv,
            LayerCustomFields: (conv.LayerCustomFields ?? []).map(layer =>
            {
              if (layer.LayerId !== layerId) return layer;
              return {
                ...layer,
                CustomFields: (layer.CustomFields ?? []).map(cf =>
                {
                  if (cf.CustomFieldId !== customFieldId) return cf;
                  return { ...cf, Value: value, FieldValue: display };
                }),
              };
            }),
          };
        }),
      );
    },
    [],
  );

  const updateConversationState = useCallback(
    (conversationId: number, updates: Partial<ConversationDetail>) =>
    {
      setConversations(prev =>
        prev.map(c => (c.ConversationId === conversationId ? { ...c, ...updates } : c)),
      );
    },
    [],
  );

  const removeConversation = useCallback((conversationId: number) =>
  {
    setConversations(prev => prev.filter(c => c.ConversationId !== conversationId));
  }, []);

  return {
    conversations,
    loading,
    loadingMore,
    hasMore,
    totalItems,
    loadMore,
    refresh: fetchInitial,
    updateField,
    updateConversationState,
    removeConversation,
  };
}
